import {Box, Button, Checkbox, MenuItem, Select, styled} from "@mui/material";
import React, {CSSProperties, useState} from "react";
import theme, {FONTS} from "../../../constants/theme";
import ThemedText from "../../text";
import {buildPath, PHONE_PREFIXES, scale, validatePhoneNumber, validateTaxCode} from "../../../utils";
import ThemedTextInput from "../../input/text";
import {findIfAlreadyRegistered, storeDonoInfo} from "../../../features/donor";
import {useNavigate} from "react-router-dom";
import {useMediaQuery} from "@mui/system";
import {Email, Phone} from "@mui/icons-material";
import Text from "../../text";

interface Props {
  style?: CSSProperties;
}

const Row = styled('div')({
  display: 'grid',
  width: scale(578.7),
  minHeight: scale(53),
  columnGap: scale(38),
  rowGap: scale(15.86 + 25),
  gridTemplateColumns: 'repeat(2, 1fr)',
  alignItems: 'start',
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: 'repeat(1, 1fr)',
    justifyItems: 'center',
    width: '100%',
    marginRight: '10px',
    gap: scale(15.86 + 28),
  },
});

const Eula = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: scale(5),
  height: scale(19),
  marginTop: scale(19 + 22),
  marginBottom: scale(37 - 18),
  [theme.breakpoints.down('sm')]: {
    width: scale(430),
    gap: scale(10),
    marginTop: scale(39 + 22),
    alignSelf: 'end',
  },
});

const ThemedButton = styled(Button)({
  borderRadius: scale(30),
  width: scale(160),
  height: scale(52),
  marginTop: scale(13),
  '&.Mui-disabled': {
    backgroundColor: theme.palette.lightGray.main,
    cursor: 'not-allowed',
  },
  [theme.breakpoints.down('sm')]: {
    order: 1
  },
});

const DonorInfoForm = ({style}: Props) => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [fistNameError, setFirstNameError] = useState(false);
  const [lastNameError, setLastNameError] = useState(false);
  const [phoneError, setPhoneError] = useState(false);
  const [cfError, setCFError] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phonePrefix, setPhonePrefix] = useState('+39');
  const [phone, setPhone] = useState('');
  const [cf, setCf] = useState('');
  const [agreeEula, setAgreeEula] = useState(false);

  const fullPhone = `${phonePrefix.trim()}${phone.trim()}`;

  const isButtonDisabled = (): boolean => {
    return (phoneError || cfError || fistNameError || lastNameError || !agreeEula)
  }

  return (
    <Box
      component="form"
      noValidate={true}
      autoComplete="true"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        height: '100%',
        marginBottom: scale(43),
        ...style,
      }}
    >
      <Row>
        <ThemedTextInput
          htmlFor={'first-name'}
          label={'Nome*'}
          placeholder={'Inserisci nome'}
          setValue={(value) => setFirstName(value)}
          setErrorValue={setFirstNameError}
          props={{autoComplete: 'no'}}
        />
        <ThemedTextInput
          htmlFor={'last-name'}
          label={'Cognome*'}
          placeholder={'Inserisci cognome'}
          setValue={(value) => setLastName(value)}
          setErrorValue={setLastNameError}
          props={{autoComplete: 'no'}}
        />
        <ThemedTextInput
          htmlFor={'phone'}
          label={'Telefono*'}
          placeholder={'Inserisci numero di telefono'}
          errorLabel={'Numero di telefono non valido'}
          validate={validatePhoneNumber}
          setValue={(value) => setPhone(value)}
          setErrorValue={setPhoneError}
          startAdornment={
            <Select
              id={'phone-prefix'}
              variant={'standard'}
              disableUnderline
              value={phonePrefix}
              onChange={(event) => setPhonePrefix(event.target.value)}
              renderValue={(val) => PHONE_PREFIXES.find((p) => p.value === val)?.name ?? val}
              MenuProps={{PaperProps: {sx: {maxHeight: scale(320)}}}}
              sx={{
                marginRight: scale(6),
                fontFamily: FONTS.MONTSERRAT,
                fontSize: scale(16),
                color: 'black',
                '& .MuiSelect-select': {
                  paddingTop: 0,
                  paddingBottom: 0,
                  paddingRight: `${scale(20)} !important`,
                  minWidth: scale(48),
                },
              }}
            >
              {PHONE_PREFIXES.map((p) => (
                <MenuItem key={p.value} value={p.value} sx={{fontFamily: FONTS.MONTSERRAT, fontSize: scale(20), color: 'black'}}>
                  {p.name}
                </MenuItem>
              ))}
            </Select>
          }
          props={{autoComplete: 'no'}}
        />
        <ThemedTextInput
          htmlFor={'cf'}
          label={'Codice fiscale'}
          placeholder={'Inserisci codice fiscale'}
          type={'CF'}
          errorLabel={'Codice fiscale non valido'}
          validate={validateTaxCode}
          setValue={(value) => setCf(value)}
          setErrorValue={setCFError}
          props={{autoComplete: 'no'}}
        />
      </Row>
      <Eula>
        <Checkbox id={'eula'} onChange={(event) => setAgreeEula(event.target.checked)} sx={{width: scale(15), height: scale(15)}}/>
        <Box
          sx={{
            display: 'flex',
            fontSize: scale(14),
            color: "inherit",
            [theme.breakpoints.down('sm')]: {
              minWidth: scale(430),
              flexWrap: 'wrap',
              whiteSpace: 'normal',
              fontSize: scale(16),
            },
          }}
        >
          <ThemedText
            text="Spuntando la casella accetti"
            fontSize={isMobile ? scale(16) : scale(14)}
            color="text"
          />
          <a
            href="https://www.aviscomunalegenova.it/privacy-policy/"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "text",
              fontWeight: '600',
              fontFamily: FONTS.MONTSERRAT,
              textDecoration: "underline",
              marginLeft: '5px',
              marginRight: '5px'
            }}
          >
            l’informativa sulla privacy.
          </a>
        </Box>
      </Eula>
      <Box
        style={{
          display: 'flex',
          justifyContent: 'center',
          flexWrap: 'wrap',
          fontSize: scale(18),
          color: "inherit",
          width: scale(819),
        }}
        sx={{
          [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            minWidth: scale(200),
            maxWidth: scale(530),
            fontSize: scale(18),
            alignItems: 'center',
            backgroundColor: theme.palette.primary.main,
            borderRadius: scale(20),
            gap: scale(20),
            marginTop: scale(20),
            paddingTop: scale(30),
            paddingBottom: scale(30),
            order: 2,
          },
        }}
      >
        <ThemedText
          text={'Per informazioni o modifiche a prenotazioni già effettuate, contatta'}
          fontSize={isMobile ? scale(22) : scale(18)}
          color={isMobile ? theme.palette.text.primary : 'text'}
          fontWeight={'500'}
          lineHeight={isMobile ? scale(26) : scale(22)}
          letterSpacing={scale(0.36)}
          sx={{textAlign: 'center', width: isMobile ? scale(420) : '100%'}}
        />
        <a
          href="https://www.aviscomunalegenova.it/"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: isMobile ? theme.palette.text.primary : 'text',
            fontSize: isMobile ? scale(22) : undefined,
            fontWeight: '600',
            fontFamily: FONTS.MONTSERRAT,
            textDecoration: "underline",
            lineHeight: scale(22),
            marginLeft: scale(5),
            marginRight: scale(5),
          }}
        >
          AVIS Comunale Genova:
        </a>
        {!isMobile ? (<ThemedText
          text={'010 355861 | 3292345449 | genova.comunale@avis.it\n'}
          fontSize={scale(18)}
          color={isMobile ? theme.palette.text.primary : 'text'}
          fontWeight={'500'}
          lineHeight={scale(22)}
          sx={{textAlign: 'center'}}
        />) : (
          <>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                color: isMobile ? theme.palette.text.primary : 'text',
              }}
            >
              <Phone sx={{ fontSize: 20 }} />
              <Text
                text={'+39 010 355861 | 3292345449'}
                fontFamily={FONTS.MONTSERRAT}
                color={isMobile ? theme.palette.text.primary : 'text'}
                fontSize={scale(24)}
                fontWeight={500}
                lineHeight={scale(30)}
                sx={{ textAlign: 'center' }}
              />
            </Box>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                color: isMobile ? theme.palette.text.primary : 'text',
              }}
            >
              <Email sx={{ fontSize: 20 }} />
              <Text
                text={'genova.comunale@avis.it'}
                fontFamily={FONTS.MONTSERRAT}
                color={isMobile ? theme.palette.text.primary : 'text'}
                fontSize={scale(24)}
                fontWeight={500}
                lineHeight={scale(30)}
                sx={{ textAlign: 'center' }}
              />
            </Box>
          </>
        )}
      </Box>
      <ThemedButton
        variant="contained"
        disabled={isButtonDisabled()}
        onClick={() => {
          const result = storeDonoInfo({donorInfo: {firstName: firstName, lastName: lastName, phone: fullPhone, CF: cf}})
          findIfAlreadyRegistered({donorInfo: {firstName: firstName, lastName: lastName, phone: fullPhone, CF: cf}}).then((result) => {
            if (result.isRegistered) {
              sessionStorage.setItem('newDonorResult', JSON.stringify({status: 'conflict', message: result.message}));
              navigate(buildPath('/donatore/gia-registrato'))
            }
          })
          if (result) {
            navigate(buildPath('/donatore/calendario'))
          }
        }}>
        <ThemedText text={'continua'} fontWeight={600} fontSize={scale(18)} fontFamily={FONTS.MONTSERRAT}/>
      </ThemedButton>
    </Box>
  )
}

export default DonorInfoForm
